import React, { FC, FormEvent, useState } from 'react';
import { useContractTemplateActions, useContractTemplates } from '../../../hooks/api/ContractTemplates/useContractTemplates';
import { KIND_LABELS, TemplateKind } from '../../../utility/contractTemplates';
import { getErrorMessages } from '../../../utility/team';
import TemplateEditor from './TemplateEditor';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

const ContractTemplatesPage: FC = () => {
  const { templates, variables, isLoading } = useContractTemplates();
  const { upload, remove } = useContractTemplateActions();
  const [ editingId, setEditingId ] = useState<string | null>(null);
  const [ name, setName ] = useState('');
  const [ kind, setKind ] = useState<TemplateKind>('base_contract');
  const [ addendumTag, setAddendumTag ] = useState('');
  const [ file, setFile ] = useState<File | null>(null);

  if (editingId) {
    return <div className="px-2"><TemplateEditor templateId={editingId} variables={variables} onClose={() => setEditingId(null)} /></div>;
  }

  const hasCommission = templates.some((t) => t.kind === 'commission' && !t.system);

  const handleUpload = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    try {
      const response = await upload.mutateAsync({ name, kind, addendum_tag: addendumTag || undefined, pdf: file });
      setName('');
      setAddendumTag('');
      setFile(null);
      setEditingId(response.data.results.id);
    } catch {
      // shown below from the mutation state
    }
  };

  return (
    <div className="px-2">
      <h1 className="text-base font-semibold leading-6 text-gray-900">Contract Templates</h1>
      <p className="mt-2 text-sm text-gray-700">
        These are the PDF forms REIX fills in for your offers. REIX provides the Texas TREC contract. You can add your own
        forms for other states, addenda (like an HOA addendum), and your broker-to-broker commission document. A licensed
        professional acting for a buyer needs a commission document set up first.
      </p>

      <ul className="mt-6 divide-y divide-gray-100 sm:max-w-3xl">
        {!isLoading && templates.length === 0 && <li className="py-2 text-sm text-gray-500">No templates yet.</li>}
        {templates.map((template) => (
          <li key={template.id} className="flex items-center justify-between gap-x-4 py-3">
            <div className="text-sm">
              <p className="font-medium text-gray-900">{template.name}{template.system && ' (provided by REIX)'}</p>
              <p className="text-gray-500">
                {KIND_LABELS[template.kind]}{template.form_number && ` - form ${template.form_number}`}{template.version && ` (${template.version})`}
                {' - '}{template.mode === 'fillable' ? 'fillable form' : 'click-to-place form'}{' - '}{template.field_count} values mapped
              </p>
            </div>
            <div className="flex gap-x-2">
              <button type="button" className={secondaryButton} onClick={() => setEditingId(template.id)}>
                {template.editable ? 'Edit' : 'View'}
              </button>
              {template.editable && (
                <button type="button" className={secondaryButton}
                  onClick={() => { if (window.confirm(`Delete "${template.name}"?`)) remove.mutate(template.id); }}>
                  Delete
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <form onSubmit={handleUpload} className="mt-8 grid gap-3 sm:max-w-3xl sm:grid-cols-2">
        <h2 className="text-sm font-semibold text-gray-900 sm:col-span-2">Add your own form</h2>
        <div>
          <label htmlFor="template_name" className="block text-sm font-medium text-gray-900">Name</label>
          <input id="template_name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="template_kind" className="block text-sm font-medium text-gray-900">What is it?</label>
          <select id="template_kind" className={inputClass} value={kind} onChange={(e) => setKind(e.target.value as TemplateKind)}>
            <option value="base_contract">{KIND_LABELS.base_contract}</option>
            <option value="addendum">{KIND_LABELS.addendum}</option>
            <option value="commission" disabled={hasCommission}>{KIND_LABELS.commission}{hasCommission ? ' (already added)' : ''}</option>
          </select>
        </div>
        {kind === 'addendum' && (
          <div>
            <label htmlFor="template_tag" className="block text-sm font-medium text-gray-900">Label (for example hoa)</label>
            <input id="template_tag" className={inputClass} value={addendumTag} onChange={(e) => setAddendumTag(e.target.value.toLowerCase())} />
          </div>
        )}
        <div className="sm:col-span-2">
          <label htmlFor="template_pdf" className="block text-sm font-medium text-gray-900">PDF file (unlocked, up to 25 MB)</label>
          <input id="template_pdf" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={upload.isPending || !file || !name.trim()} className={primaryButton}>Upload and set up</button>
          {upload.error && <ul className="mt-2 text-sm text-red-600">{getErrorMessages(upload.error).map((m) => <li key={m}>{m}</li>)}</ul>}
        </div>
      </form>
    </div>
  );
};

export default ContractTemplatesPage;
